import React, { useState } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import * as XLSX from "xlsx";

const RETURNS_COL = "returns_v2";
const PAGE_SIZE = 500;

// -------- helpers --------
function tsToIso(ts) {
  if (!ts) return "";
  if (typeof ts?.toDate === "function") return ts.toDate().toISOString();
  if (typeof ts === "string") return ts; // sometimes old data
  return "";
}

function getFormFromDoc(d) {
  if (!d) return {};
  if (d.type === "cyl") return d.cyl?.form || {};
  if (d.type === "parts") return d.parts?.form || {};
  return d.cyl?.form || d.parts?.form || {};
}

function safeString(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}

function bool01(v) {
  return v ? 1 : 0;
}

function joinList(arr) {
  if (!Array.isArray(arr)) return "";
  return arr.map((x) => String(x)).filter(Boolean).join(", ");
}

function flattenCylRow(d) {
  const f = getFormFromDoc(d);
  const leaks = d.cyl?.leaks || {};
  const check = d.cyl?.cylCheck || {};
  const parts = d.cyl?.parts || {};
  const returnedParts = d.cyl?.returnedParts || [];

  return {
    docId: d.id || "",
    type: d.type || "cyl",
    archive: d.archive === true ? "true" : "false",
    archivedAt: tsToIso(d.archivedAt),
    createdAt: tsToIso(d.createdAt),
    updatedAt: tsToIso(d.updatedAt),
    submittedAt: tsToIso(d.submittedAt),
    status: safeString(d.status),
    statusUpdatedAt: tsToIso(d.statusUpdatedAt),
    completedAt: tsToIso(d.completedAt),

    source: safeString(d.source),
    version: safeString(d.version),

    // identifiers
    returnsNoteNumber: safeString(f.returnsNoteNumber),
    serialNumber: safeString(f.serialNumber),
    sretNumber: safeString(f.sretNumber),

    // cylinder info
    cylinderModel: safeString(f.cylinderModel),
    capacity: safeString(f.capacity),
    dateOfManufacture: safeString(f.dateOfManufacture),

    // coded reasons
    reasonCode: safeString(f.reasonCode),
    reasonDefectCode: safeString(f.reasonDefectCode),
    faultFoundCode: safeString(f.faultFoundCode),
    faultDefectCode: safeString(f.faultDefectCode),

    // operators
    operator_welded: safeString(f.operators?.welded),
    operator_airTest: safeString(f.operators?.airTest),
    operator_hotTested: safeString(f.operators?.hotTested),
    operator_foamTested: safeString(f.operators?.foamTested),

    // checklist booleans (0/1)
    cyl_cylinderInstalled: bool01(check.cylinderInstalled),
    cyl_damageToCase: bool01(check.damageToCase),
    cyl_damageToPackaging: bool01(check.damageToPackaging),
    cyl_damageToBosses: bool01(check.damageToBosses),
    cyl_damageToPipeworkParts: bool01(check.damageToPipeworkParts),
    cyl_damageToFoam: bool01(check.damageToFoam),
    cyl_stripAndTest: bool01(check.stripAndTest),

    // leaks booleans (0/1) + other comments
    leak_verticalSeam: bool01(leaks.verticalSeam),
    leak_statPocket: bool01(leaks.statPocket),
    leak_topSeam: bool01(leaks.topSeam),
    leak_coilBoss: bool01(leaks.coilBoss),
    leak_bottomSeam: bool01(leaks.bottomSeam),
    leak_coilBody: bool01(leaks.coilBody),
    leak_hotDrawOff: bool01(leaks.hotDrawOff),
    leak_bodySheet: bool01(leaks.bodySheet),
    leak_secondaryReturn: bool01(leaks.secondaryReturn),
    leak_tAndPRelief: bool01(leaks.tAndPRelief),
    leak_coldFeed: bool01(leaks.coldFeed),
    leak_immersionBoss: bool01(leaks.immersionBoss),
    leak_other: bool01(leaks.other),
    leak_otherComments: safeString(leaks.otherComments),

    // parts return section (mode + list)
    parts_noPartsReturned: bool01(parts.noPartsReturned),
    parts_allPartsReturned: bool01(parts.allPartsReturned),
    parts_partialReturn: bool01(parts.partialReturn),
    parts_systemType: safeString(parts.systemType),
    parts_rmaRef: safeString(parts.rmaRef),
    parts_notes: safeString(parts.notes),
    returnedParts: joinList(returnedParts),

    // notes
    internalNotes: safeString(f.internalNotes),
    adminNotes: safeString(d.adminNotes),
    notesUpdatedAt: tsToIso(d.notesUpdatedAt),

    // storage
    storageProvider: safeString(d.storage?.provider),
    folderId: safeString(d.storage?.folderId),
    folderUrl: safeString(d.storage?.folderUrl),
  };
}

function flattenPartsRow(d) {
  const f = getFormFromDoc(d);
  const t = d.parts?.templateSnapshot || {};
  const test = d.parts?.test || {};

  return {
    docId: d.id || "",
    type: d.type || "parts",
    archive: d.archive === true ? "true" : "false",
    archivedAt: tsToIso(d.archivedAt),
    createdAt: tsToIso(d.createdAt),
    updatedAt: tsToIso(d.updatedAt),
    submittedAt: tsToIso(d.submittedAt),
    status: safeString(d.status),
    statusUpdatedAt: tsToIso(d.statusUpdatedAt),
    completedAt: tsToIso(d.completedAt),

    source: safeString(d.source),
    version: safeString(d.version),

    // identifiers
    returnsNoteNumber: safeString(f.returnsNoteNumber),
    serialNumber: safeString(f.serialNumber),
    sretNumber: safeString(f.sretNumber),

    // part info
    partCode: safeString(f.partCode),
    partTitle: safeString(f.partTitle || t.title),
    partDescription: safeString(f.partDescription || t.description),

    // codes
    reportedCode: safeString(f.reportedCode),
    faultFoundCode: safeString(f.faultFoundCode),

    // notes
    internalNotes: safeString(f.internalNotes),
    adminNotes: safeString(d.adminNotes),
    notesUpdatedAt: tsToIso(d.notesUpdatedAt),

    // template snapshot meta (optional)
    templateTitle: safeString(t.title),
    templateDescription: safeString(t.description),
    templateCodesCount: Array.isArray(t.codes) ? t.codes.length : "",
    templateStepsCount: Array.isArray(t.steps) ? t.steps.length : "",

    // test results as JSON string (so you don’t lose anything)
    testJson: safeString(Object.keys(test).length ? JSON.stringify(test) : ""),

    // storage
    storageProvider: safeString(d.storage?.provider),
    folderId: safeString(d.storage?.folderId),
    folderUrl: safeString(d.storage?.folderUrl),
  };
}

// -------- component --------
export default function ReturnsV2ExcelExporter() {
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [err, setErr] = useState("");

  const addLog = (line) => setLog((p) => [line, ...p].slice(0, 250));

  async function loadAllReturnsV2() {
    const all = [];
    let last = null;
    let page = 0;

    while (true) {
      const qy = last
        ? query(
            collection(db, RETURNS_COL),
            orderBy("createdAt", "desc"),
            startAfter(last),
            limit(PAGE_SIZE)
          )
        : query(collection(db, RETURNS_COL), orderBy("createdAt", "desc"), limit(PAGE_SIZE));

      const snap = await getDocs(qy);
      if (snap.empty) break;

      page += 1;
      addLog(`Loaded page ${page} (${snap.size} docs)`);

      snap.docs.forEach((d) => all.push({ id: d.id, _snap: d, ...d.data() }));
      last = snap.docs[snap.docs.length - 1];

      if (snap.size < PAGE_SIZE) break;
    }

    return all;
  }

  async function exportExcel() {
    setBusy(true);
    setErr("");
    setLog([]);

    try {
      addLog("Starting export…");
      const docs = await loadAllReturnsV2();
      addLog(`Total loaded: ${docs.length}`);

      const cylRows = [];
      const partsRows = [];

      for (const d of docs) {
        if (d.type === "parts") partsRows.push(flattenPartsRow(d));
        else cylRows.push(flattenCylRow(d)); // default anything else to cyl
      }

      addLog(`Cylinders: ${cylRows.length}`);
      addLog(`Parts: ${partsRows.length}`);

      const wb = XLSX.utils.book_new();

      const wsCyl = XLSX.utils.json_to_sheet(cylRows);
      XLSX.utils.book_append_sheet(wb, wsCyl, "Cylinders");

      const wsParts = XLSX.utils.json_to_sheet(partsRows);
      XLSX.utils.book_append_sheet(wb, wsParts, "Parts");

      // optional: a summary sheet (handy)
      const summary = [
        { metric: "Exported at", value: new Date().toISOString() },
        { metric: "Total docs", value: docs.length },
        { metric: "Cylinders", value: cylRows.length },
        { metric: "Parts", value: partsRows.length },
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), "Summary");

      const filename = `returns_v2_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, filename);

      addLog(`DONE. Downloaded: ${filename}`);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Export failed.");
      addLog("FAILED.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-bold text-gray-900">Export returns_v2 → Excel</h1>

        <button
          type="button"
          onClick={exportExcel}
          disabled={busy}
          className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {busy ? "Exporting…" : "Export to .xlsx"}
        </button>
      </div>

      {err ? <div className="text-sm text-red-600">{err}</div> : null}

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="text-sm text-gray-700 mb-2">
          This will export <b>all</b> docs from <code>{RETURNS_COL}</code> into:
          <ul className="list-disc pl-5 mt-2 text-sm text-gray-700 space-y-1">
            <li><b>Cylinders</b> sheet</li>
            <li><b>Parts</b> sheet</li>
            <li><b>Summary</b> sheet</li>
          </ul>
        </div>

        <div className="mt-3">
          <div className="text-sm font-semibold mb-2">Log</div>
          {log.length === 0 ? (
            <div className="text-sm text-gray-500">No activity yet.</div>
          ) : (
            <div className="max-h-80 overflow-auto text-xs space-y-1">
              {log.map((l, i) => (
                <div key={i} className="font-mono text-gray-700">{l}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
